import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ChefCoinIcon from '@/components/ui/ChefCoinIcon';
import { StudioGenerationOverlay } from "@/components/studio/StudioGenerationOverlay";
import RecipeTeaser from "@/components/studio/RecipeTeaser";
import { useNavigate } from "react-router-dom";
import { useRecipeStudio } from "./RecipeStudioContext";
import { handleStudioRecipeGenerationWrapper as handleStudioRecipeGeneration } from "@/services/assistantService";
import { useAuth } from "@/contexts/AuthContext";
import { incrementStudioUsage, estimateTokenUsage, trackTokenUsage } from "@/lib/dashboard-service";
import { getCostForAction, spendCoins } from "@/services/chefcoinService";
import { UpgradeModal } from "@/components/common/UpgradeModal";
import ProfileCompletionModal from '@/components/ProfileCompletionModal';
import { ErrorModal, useErrorModal } from '@/components/common/ErrorModal';
import { Recipe } from "@/types/recipe";
import { chefPersonas } from "@/config/chefPersonas";
import { getTextGenerationModel } from "@/services/modelService";
import { startStudioImageJob } from "@/services/studioImageJob";
import BottomActionBar from "./BottomActionBar";
import { motion } from "framer-motion";
import { ChefHat, Sparkles, Leaf, MessageSquare, Globe, Gauge } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatErrorForUser } from "@/utils/errorUtils";

const Step4Review: React.FC = () => {
  const {
    coreRecipeQuery,
    selectedPersona,
    complexity,
    selectedDietaryTags,
    specialRequest,
    language,
    languageCustom,
  } = useRecipeStudio();
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeModelId, setActiveModelId] = useState<string | undefined>();

  const { user, chefcoins_balance, setChefcoinsBalance, isGuest, userProfile } = useAuth();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const { showError, modalProps: errorModalProps } = useErrorModal();

  // Standard action cost - model selection is now admin-controlled
  const cost = getCostForAction('STUDIO_GENERATE');

  const handleBack = () => navigate("../step/3");

  const handleGenerateRecipe = async () => {
    if (isGuest()) {
      setShowProfileModal(true);
      return;
    }

    if (chefcoins_balance !== null && chefcoins_balance < cost) {
      setIsUpgradeModalOpen(true);
      return;
    }

    setIsGenerating(true);

    if (user?.id) {
      try {
        const spendResult = await spendCoins(user.id, cost, { feature: 'STUDIO_GENERATE' });
        if (!spendResult.success) {
          throw new Error(spendResult.error);
        }
        setChefcoinsBalance(spendResult.newBalance);
      } catch (error) {
        showError(
          error instanceof Error ? error.message : "Unknown error",
          "Transaction Failed"
        );
        setIsGenerating(false);
        return;
      }
    } else {
      showError(
        "Could not identify user. Please try logging in again.",
        "Authentication Error"
      );
      setIsGenerating(false);
      return;
    }

    try {
      let complexityEnum: "Simple" | "Standard" | "Ambitious";
      switch (complexity) {
        case 1: complexityEnum = "Simple"; break;
        case 3: complexityEnum = "Ambitious"; break;
        default: complexityEnum = "Standard";
      }

      const modelToUse = await getTextGenerationModel();
      setActiveModelId(modelToUse);

      const studioOptions = {
        persona: selectedPersona,
        complexity: complexityEnum,
        dietaryTags: selectedDietaryTags,
        specialRequest,
        coreRecipeQuery,
        language,
        languageCustom,
        model: modelToUse,
      };

      // Start recipe generation
      const recipePromise = handleStudioRecipeGeneration(studioOptions);

      const result = await recipePromise;
      if (result.success && result.data?.formData) {
        const { formData } = result.data;

        if (user?.id) {
          incrementStudioUsage(user.id);
          const estimatedTokens = estimateTokenUsage(
            JSON.stringify(studioOptions),
            `${formData.ingredients || ''}\n${formData.method || ''}`
          );
          trackTokenUsage(user.id, estimatedTokens);
        }

        // The service layer already parsed the AI output (JSON-first with a
        // markdown fallback) — trust it instead of re-parsing here. Only the
        // title needs a final fallback chain so it is never empty.
        const finalTitle = formData.title?.trim() || coreRecipeQuery || 'Untitled Recipe';

        const updatedFormData = {
          ...formData,
          title: finalTitle,
          autoGenerateImage: false, // Image generation is handled below
          fromStudio: true,
          studioPersona: selectedPersona,
        };

        // Save recipe data to sessionStorage immediately
        sessionStorage.setItem("studioRecipeData", JSON.stringify(updatedFormData));

        // Start image generation in the background via the shared job module.
        // The review page awaits this promise at finalize time so the image
        // can no longer be silently discarded if the user is quick.
        if (updatedFormData.title && updatedFormData.ingredients) {

          const tempRecipe: Recipe = {
            id: 'temp-studio-recipe',
            title: updatedFormData.title,
            description: updatedFormData.description || '',
            category: updatedFormData.category,
            ingredients: updatedFormData.ingredients,
            method: updatedFormData.method || '',
            notes: updatedFormData.notes || '',
            tags: updatedFormData.tags || [],
            dateCreated: new Date().toISOString(),
            dateModified: new Date().toISOString(),
            isFavorite: false,
            share: true,
            image_url: '',
            has_custom_image: false,
            user_id: '',
            isOwner: true
          };

          // Start image generation without waiting for it — provider is resolved
          // from admin settings inside generateImageDirectlyForRecipe.
          startStudioImageJob(tempRecipe).then(imageResult => {
            if (imageResult.success && imageResult.imageData) {
              // Update sessionStorage with image data
              const currentData = sessionStorage.getItem("studioRecipeData");
              if (currentData) {
                try {
                  const parsedData = JSON.parse(currentData);
                  const updatedDataWithImage = {
                    ...parsedData,
                    image_url: imageResult.imageData,
                    has_custom_image: true,
                    studioGeneratedImage: true
                  };
                  sessionStorage.setItem("studioRecipeData", JSON.stringify(updatedDataWithImage));

                  // Trigger a custom event to notify RecipeReviewPage that image is ready
                  window.dispatchEvent(new CustomEvent('studioImageGenerated', {
                    detail: { imageData: imageResult.imageData }
                  }));
                } catch {
                }
              }
            } else {
              // Trigger event to notify that image generation failed
              window.dispatchEvent(new CustomEvent('studioImageGenerationFailed', {
                detail: { error: imageResult.error }
              }));
            }
          }).catch(imageError => {
            window.dispatchEvent(new CustomEvent('studioImageGenerationFailed', {
              detail: { error: imageError.message }
            }));
          });
        }

        // Navigate immediately to Chef's Table
        navigate("../step/5");
      } else {
        throw new Error(result?.error || "Unknown error during recipe generation");
      }
    } catch (error) {
      showError(
        formatErrorForUser(error instanceof Error ? error.message : "Please try again."),
        "Recipe Generation Failed"
      );
    } finally {
      setIsGenerating(false);
    }
  };

  if (!coreRecipeQuery.trim()) {
    return (
      <div className="max-w-xl mx-auto mt-10 p-6 bg-yellow-50 border border-yellow-300 rounded-lg text-yellow-900 text-center">
        <p>
          <strong>Missing recipe idea!</strong><br />
          Please go back to <span className="underline cursor-pointer" onClick={() => navigate('../step/1')}>Step 1</span> and tell us what you'd like to make before reviewing your recipe.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto w-full">
      {/* Show the same warning modal as in AssistantWidget, with Go to Profile button */}
      {showProfileModal && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white border border-red-400 rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md w-full text-center"
          >
            <div className="text-lg font-bold text-red-700 mb-4">Profile Required</div>
            <div className="text-slate-700 mb-6">
              Please complete your profile to use the AI assistant and premium features.
            </div>
            <div className="flex flex-col gap-3 items-center">
              <button
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-3 rounded-lg w-full transition-colors min-h-[44px]"
                onClick={() => { setShowProfileModal(false); setShowProfileCompletion(true); }}
              >
                Go to Profile
              </button>
              <button
                className="bg-slate-200 hover:bg-slate-300 text-slate-800 font-semibold px-6 py-3 rounded-lg w-full transition-colors min-h-[44px]"
                onClick={() => setShowProfileModal(false)}
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
      <ProfileCompletionModal
        isOpen={showProfileCompletion}
        onComplete={() => setShowProfileCompletion(false)}
      />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-6"
      >
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Ready to Create
        </h1>
        <p className="text-slate-600">
          Review your selections and generate your recipe
        </p>
      </motion.div>

      {/* Chef Card - Hero section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="mb-4"
      >
        {(() => {
          const persona = chefPersonas.find(p => p.id === selectedPersona);
          if (!persona) return null;
          return (
            <Card className="overflow-hidden border-0 shadow-xl bg-gradient-to-br from-orange-500 to-pink-500">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-4">
                  <div
                    className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-white/30 bg-white shadow-lg flex-shrink-0"
                    style={{
                      backgroundImage: `url(${persona.avatar})`,
                      backgroundSize: '140%',
                      backgroundPosition: 'center 20%',
                      backgroundRepeat: 'no-repeat'
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <ChefHat className="w-4 h-4 text-white/80" />
                      <span className="text-white/80 text-xs uppercase tracking-wide font-medium">Your Chef</span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-white truncate">{persona.name}</h3>
                    <p className="text-white/80 text-sm line-clamp-2 mt-1">{persona.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}
      </motion.div>

      {/* Recipe Request Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="mb-4"
      >
        <Card className="border-0 shadow-lg bg-white">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-orange-100 text-orange-600 shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium mb-1">Recipe Request</p>
                <p className="text-lg font-semibold text-slate-900">{coreRecipeQuery}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Details Grid */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-2 gap-3 mb-6"
      >
        {/* Complexity */}
        <Card className="border-0 shadow-md bg-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Gauge className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-slate-500">Complexity</span>
            </div>
            <p className={cn(
              "font-semibold",
              complexity === 1 && "text-green-600",
              complexity === 2 && "text-blue-600",
              complexity === 3 && "text-purple-600"
            )}>
              {complexityEnumLabel(complexity)}
            </p>
          </CardContent>
        </Card>

        {/* Language */}
        <Card className="border-0 shadow-md bg-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4 text-teal-500" />
              <span className="text-xs text-slate-500">Language</span>
            </div>
            <p className="font-semibold text-slate-900 truncate">
              {language === "Other (type manually)" ? (languageCustom?.trim() || "Other") : (language || "English (UK)")}
            </p>
          </CardContent>
        </Card>

        {/* Dietary Tags */}
        <Card className="border-0 shadow-md bg-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Leaf className="w-4 h-4 text-green-500" />
              <span className="text-xs text-slate-500">Dietary</span>
            </div>
            <p className="font-semibold text-slate-900 truncate">
              {selectedDietaryTags.length > 0 ? selectedDietaryTags.slice(0, 2).join(", ") + (selectedDietaryTags.length > 2 ? ` +${selectedDietaryTags.length - 2}` : "") : "None"}
            </p>
          </CardContent>
        </Card>

        {/* Special Request */}
        <Card className="border-0 shadow-md bg-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-slate-500">Notes</span>
            </div>
            <p className="font-semibold text-slate-900 truncate">
              {specialRequest ? "Added" : "None"}
            </p>
          </CardContent>
        </Card>
      </motion.div>

      {/* Recipe Teaser - Preview before generation */}
      <RecipeTeaser
        query={coreRecipeQuery}
        complexity={complexity}
        dietaryTags={selectedDietaryTags}
        chefId={selectedPersona}
        className="mb-6"
      />

      {/* Full-screen generation overlay (modal) */}
      <StudioGenerationOverlay
        isOpen={isGenerating}
        personaId={selectedPersona}
        personaName={chefPersonas.find(p => p.id === selectedPersona)?.name}
        recipeQuery={coreRecipeQuery}
        modelLabel={activeModelId}
      />

      {/* Desktop buttons - hidden when generating */}
      {!isGenerating && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="hidden md:flex justify-between gap-4 mt-8"
        >
          <Button variant="outline" onClick={handleBack} className="flex-1 max-w-[200px] h-12">
            Back
          </Button>
          <Button
            onClick={handleGenerateRecipe}
            className="flex-1 max-w-[260px] h-12 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 flex items-center justify-center gap-2 text-base shadow-lg hover:shadow-xl transition-all"
          >
            <Sparkles className="h-4 w-4" />
            <span>Generate Recipe</span>
            <div className="flex items-center gap-1 bg-black/20 rounded-full px-2.5 py-0.5 ml-1">
              <ChefCoinIcon className="h-5 w-5" />
              <span className="font-bold">{cost}</span>
            </div>
          </Button>
        </motion.div>
      )}

      {/* Mobile bottom action bar - hidden when generating */}
      {!isGenerating && (
        <BottomActionBar
          onBack={handleBack}
          onNext={handleGenerateRecipe}
          nextLabel="Generate"
          showCost={cost}
          hideOnDesktop
        />
      )}

      <UpgradeModal isOpen={isUpgradeModalOpen} onOpenChange={setIsUpgradeModalOpen} featureName="Studio Recipe Generation" subscription={userProfile?.subscription ?? 'free'} />

      <ErrorModal {...errorModalProps} />
    </div>
  );
};

function complexityEnumLabel(complexity: number) {
  if (complexity === 1) return "Simple";
  if (complexity === 3) return "Ambitious";
  return "Standard";
}

export default Step4Review;
